'use client';

import React from 'react';

// ============================================================================
// Size and Variant configurations (no Tailwind CSS dependency)
// ============================================================================

const SELECT_SIZES = {
  sm: { height: '32px', fontSize: '12px', padding: '6px 8px' },
  md: { height: '40px', fontSize: '14px', padding: '8px 12px' },
  lg: { height: '48px', fontSize: '16px', padding: '10px 16px' },
} as const;

interface SelectVariantConfig {
  border: string;
  backgroundColor: string;
  focusBorderColor: string;
  borderBottom?: string;
  borderRadius?: string;
  padding?: string;
  boxShadow?: string;
}

const SELECT_VARIANTS: Record<string, SelectVariantConfig> = {
  outline: {
    border: '1px solid #D1D5DB',
    backgroundColor: '#FFFFFF',
    focusBorderColor: '#3B82F6',
  },
  filled: {
    border: '1px solid transparent',
    backgroundColor: '#F3F4F6',
    focusBorderColor: '#3B82F6',
  },
  flushed: {
    border: 'none',
    borderBottom: '2px solid #D1D5DB',
    backgroundColor: 'transparent',
    borderRadius: '0',
    padding: '0',
    focusBorderColor: '#3B82F6',
  },
  unstyled: {
    border: 'none',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    padding: '0',
    focusBorderColor: '#3B82F6',
  },
};

// ============================================================================
// Components
// ============================================================================

export interface NativeSelectFieldProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'size'
> {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'outline' | 'filled' | 'flushed' | 'unstyled';
  invalid?: boolean;
}

const NativeSelectField = React.forwardRef<HTMLSelectElement, NativeSelectFieldProps>(
  ({ size = 'md', variant = 'outline', invalid, style, ...props }, ref) => {
    const sizeConfig = SELECT_SIZES[size] || SELECT_SIZES.md;
    const variantConfig = SELECT_VARIANTS[variant] || SELECT_VARIANTS.outline;

    const selectStyle: React.CSSProperties = {
      width: '100%',
      height: sizeConfig.height,
      boxSizing: 'border-box',
      padding: variantConfig.padding || sizeConfig.padding,
      paddingRight: '32px',
      fontSize: sizeConfig.fontSize,
      lineHeight: '1.5',
      borderRadius: variantConfig.borderRadius !== undefined ? variantConfig.borderRadius : '6px',
      border: variantConfig.border,
      ...(variantConfig.borderBottom ? { borderBottom: variantConfig.borderBottom } : {}),
      backgroundColor: variantConfig.backgroundColor,
      boxShadow: variantConfig.boxShadow ?? 'none',
      transition: 'border-color 150ms ease, box-shadow 150ms ease',
      outline: 'none',
      cursor: props.disabled ? 'not-allowed' : 'pointer',
      opacity: props.disabled ? 0.5 : 1,
      appearance: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      ...(invalid && {
        borderColor: '#EF4444',
      }),
      ...style,
    };

    return <select ref={ref} style={selectStyle} aria-invalid={invalid} {...props} />;
  }
);

NativeSelectField.displayName = 'NativeSelectField';

export interface NativeSelectRootProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'outline' | 'filled' | 'flushed' | 'unstyled';
  invalid?: boolean;
  disabled?: boolean;
}

const NativeSelectRoot = React.forwardRef<HTMLDivElement, NativeSelectRootProps>(
  (
    {
      children,
      style,
      size: _size,
      variant: _variant,
      invalid: _invalid,
      disabled: _disabled,
      ...props
    },
    ref
  ) => {
    // `size`/`variant`/`invalid`/`disabled` describe the Field, not this
    // positioning wrapper — consume them here so they never leak onto the DOM
    // `<div>` (React warns on non-boolean attributes like `invalid={false}`).
    const rootStyle: React.CSSProperties = {
      position: 'relative',
      width: '100%',
      ...style,
    };

    return (
      <div ref={ref} style={rootStyle} {...props}>
        {children}
      </div>
    );
  }
);

NativeSelectRoot.displayName = 'NativeSelectRoot';

const NativeSelectIndicator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ style, ...props }, ref) => {
  const indicatorStyle: React.CSSProperties = {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...style,
  };

  return (
    <div ref={ref} style={indicatorStyle} {...props}>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.5 4.5L6 8L9.5 4.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
});

NativeSelectIndicator.displayName = 'NativeSelectIndicator';

/**
 * @deprecated This component is deprecated since v0.8.72.
 * Use `UnifiedCombobox` or `ComboboxField` from '../index' instead.
 *
 * Migration:
 * - Standalone: `<UnifiedCombobox<string> options={opts} value={v} onChange={fn} />`
 * - React Hook Form: `<ComboboxField control={control} name="field" options={opts} />`
 *
 * @see /docs/planning/combobox/migration-guide.md
 */
export const NativeSelect = {
  Root: NativeSelectRoot,
  Field: NativeSelectField,
  Indicator: NativeSelectIndicator,
};
